import { useState, useMemo, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { subDays, endOfDay, startOfDay } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { PondMultiSelect } from '@/features/shared/components/trends/PondMultiSelect';
import { ViewToggle } from '@/features/shared/components/trends/ViewToggle';
import { TrendChart } from '@/features/shared/components/trends/TrendChart';
import { DateRangeFilter } from '@/features/shared/components/DateRangeFilter';
import type { ViewMode } from '@/features/shared/components/trends/ViewToggle';
import type { TrendSeries } from '@/features/shared/components/trends/TrendChart';
import type { DateRange } from '@/features/shared/components/DateRangeFilter';
import { waterQualityApi } from '../api/waterQualityApi';
import { Card } from '@/shared/components/ui/Card';
import { SectionLoading } from '@/shared/components/ui/LoadingSpinner';
import type { PondResponse } from '@/shared/types/api';

interface WaterQualityTrendsTabProps {
  farmId: string;
  ponds: PondResponse[];
}

type WaterQualityMetric = 'dissolvedOxygen' | 'ph' | 'temperature' | 'salinity' | 'ammonia' | 'nitrite' | 'nitrate';

const METRICS: Array<{ key: WaterQualityMetric; label: string; unit: string }> = [
  { key: 'dissolvedOxygen', label: 'Dissolved Oxygen', unit: 'mg/L' },
  { key: 'ph', label: 'pH', unit: '' },
  { key: 'temperature', label: 'Temperature', unit: '°C' },
  { key: 'salinity', label: 'Salinity', unit: 'ppt' },
  { key: 'ammonia', label: 'Ammonia', unit: 'mg/L' },
  { key: 'nitrite', label: 'Nitrite', unit: 'mg/L' },
  { key: 'nitrate', label: 'Nitrate', unit: 'mg/L' },
];

const POND_COLORS = ['#10B981', '#3B82F6', '#F59E0B']; // Emerald, Blue, Amber

export const WaterQualityTrendsTab: React.FC<WaterQualityTrendsTabProps> = ({
  farmId,
  ponds,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL query params
  const urlPondIds = searchParams.get('ponds')?.split(',').filter(Boolean) || [];
  const urlMetrics = searchParams.get('metrics')?.split(',').filter(Boolean) as WaterQualityMetric[] || ['dissolvedOxygen', 'ph'];
  const urlViewMode = (searchParams.get('view') || 'overlay') as ViewMode;
  const urlStartDate = searchParams.get('startDate')
    ? new Date(searchParams.get('startDate')!)
    : startOfDay(subDays(new Date(), 7));
  const urlEndDate = searchParams.get('endDate')
    ? new Date(searchParams.get('endDate')!)
    : endOfDay(new Date());

  const [selectedPondIds, setSelectedPondIds] = useState<string[]>(urlPondIds);
  const [selectedMetrics, setSelectedMetrics] = useState<WaterQualityMetric[]>(urlMetrics);
  const [viewMode, setViewMode] = useState<ViewMode>(urlViewMode);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: urlStartDate,
    endDate: urlEndDate,
  });

  // Sync all filter state to URL
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Set pond IDs
    if (selectedPondIds.length > 0) {
      newParams.set('ponds', selectedPondIds.join(','));
    }

    // Set metrics
    if (selectedMetrics.length > 0) {
      newParams.set('metrics', selectedMetrics.join(','));
    }

    // Set view mode
    newParams.set('view', viewMode);

    // Set date range
    newParams.set('startDate', dateRange.startDate.toISOString());
    newParams.set('endDate', dateRange.endDate.toISOString());

    setSearchParams(newParams, { replace: true });
  }, [selectedPondIds, selectedMetrics, viewMode, dateRange, setSearchParams]);

  // Fetch data for each selected pond in parallel
  const pondQueries = useQueries({
    queries: selectedPondIds.map((pondId) => ({
      queryKey: ['water-quality', 'trends', farmId, pondId, dateRange.startDate, dateRange.endDate],
      queryFn: () =>
        waterQualityApi.listByFarm(farmId, {
          pondId,
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString(),
          limit: 100,
          sortOrder: 'asc' as const,
        }),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = pondQueries.some((q) => q.isLoading);
  const hasError = pondQueries.some((q) => q.isError);

  // Transform data into series format for each metric
  const metricSeries = useMemo(() => {
    const seriesByMetric = new Map<WaterQualityMetric, TrendSeries[]>();

    selectedMetrics.forEach((metric) => {
      const series: TrendSeries[] = [];

      selectedPondIds.forEach((pondId, index) => {
        const query = pondQueries[index];
        const pond = ponds.find((p) => p.id === pondId);

        if (query.data?.data && pond) {
          const data = query.data.data
            .filter((record) => record[metric] !== undefined && record[metric] !== null)
            .map((record) => ({
              timestamp: record.testedAt,
              value: record[metric] as number,
              pondId,
              pondName: pond.name,
            }));

          series.push({
            pondId,
            pondName: pond.name,
            data,
            color: POND_COLORS[index % POND_COLORS.length],
          });
        }
      });

      seriesByMetric.set(metric, series);
    });

    return seriesByMetric;
  }, [selectedPondIds, selectedMetrics, pondQueries, ponds]);

  const handleMetricToggle = (metricKey: WaterQualityMetric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metricKey)
        ? prev.filter((m) => m !== metricKey)
        : [...prev, metricKey]
    );
  };

  const handleZoomSelect = (range: DateRange) => {
    setDateRange({
      startDate: startOfDay(range.startDate),
      endDate: endOfDay(range.endDate),
    });
  };

  const pondOptions = ponds.map((p) => ({ id: p.id, name: p.name }));

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pond Selection */}
            <PondMultiSelect
              ponds={pondOptions}
              selectedPondIds={selectedPondIds}
              onChange={setSelectedPondIds}
              maxSelection={3}
              label="Select Ponds to Compare"
            />

            {/* Date Range */}
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
              maxDays={30}
              label="Date Range"
            />
          </div>

          {/* Metric Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Metrics
            </label>
            <div className="flex flex-wrap gap-2">
              {METRICS.map((metric) => (
                <button
                  key={metric.key}
                  onClick={() => handleMetricToggle(metric.key)}
                  className={`
                    px-4 py-2 rounded-lg border text-sm font-medium transition-all
                    ${
                      selectedMetrics.includes(metric.key)
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-700 border-slate-200/50 hover:border-primary-300 hover:bg-gray-50'
                    }
                  `}
                >
                  {metric.label}
                </button>
              ))}
            </div>
            {selectedMetrics.length === 0 && (
              <p className="mt-2 text-xs text-amber-600">
                Please select at least one metric to display
              </p>
            )}
          </div>

          {/* View Toggle */}
          {selectedPondIds.length > 1 && selectedMetrics.length > 0 && (
            <ViewToggle viewMode={viewMode} onChange={setViewMode} />
          )}
        </div>
      </Card>

      {/* Charts */}
      {selectedPondIds.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Select ponds to view trends
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              Choose up to 3 ponds and metrics to compare water quality trends over time.
            </p>
          </div>
        </Card>
      ) : selectedMetrics.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Select metrics to display
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              Choose at least one metric to view trends for the selected ponds.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <SectionLoading message="Loading trend data..." />
      ) : hasError ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 border border-red-200/50 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Failed to load trend data
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              There was an error loading the trend data. Please try again.
            </p>
          </div>
        </Card>
      ) : viewMode === 'overlay' ? (
        // Overlay mode: All ponds on one chart per metric
        <div className="space-y-6">
          {selectedMetrics.map((metricKey) => {
            const metric = METRICS.find((m) => m.key === metricKey);
            const series = metricSeries.get(metricKey) || [];
            return (
              <TrendChart
                key={metricKey}
                series={series}
                metricLabel={metric?.label || metricKey}
                metricUnit={metric?.unit}
                height={350}
                onZoomSelect={handleZoomSelect}
              />
            );
          })}
        </div>
      ) : (
        // Separate mode: Each pond gets its own chart for each metric
        <div className="space-y-6">
          {selectedPondIds.map((pondId, pondIndex) => {
            const pond = ponds.find((p) => p.id === pondId);
            if (!pond) return null;

            return (
              <div key={pondId} className="space-y-4">
                <h3 className="font-roboto text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: POND_COLORS[pondIndex % POND_COLORS.length] }}
                  />
                  {pond.name}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {selectedMetrics.map((metricKey) => {
                    const metric = METRICS.find((m) => m.key === metricKey);
                    const allSeries = metricSeries.get(metricKey) || [];
                    const pondSeries = allSeries.filter((s) => s.pondId === pondId);

                    return (
                      <TrendChart
                        key={`${pondId}-${metricKey}`}
                        series={pondSeries}
                        metricLabel={metric?.label || metricKey}
                        metricUnit={metric?.unit}
                        height={250}
                        onZoomSelect={handleZoomSelect}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
